/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useCallback, useState } from "react";
import { TouchableOpacity, View, Image } from "react-native";
import LinearGradient from "react-native-web-linear-gradient";

import { SummitType } from "graphql/internal/generated";
import { Text, useInterval } from "UpduoLibrary";
import {
  skaleWhite,
  upduoSummitGradientBlue,
  upduoSummitGradientGray,
  upduoSummitGradientOrange,
  upduoSummitGradientPurple,
} from "config/UpduoColors";
import {
  getSummitColor,
  SummitColor,
  getResizedImageUrl,
} from "config/Utilities";
import CountdownTimer from "UpduoLibrary/CountdownTimer";

function getSummitGradientColor(summit: SummitType) {
  const color = getSummitColor(summit);

  switch (color) {
    case SummitColor.ORANGE:
      return upduoSummitGradientOrange;
    case SummitColor.BLUE:
      return upduoSummitGradientBlue;
    case SummitColor.PURPLE:
      return upduoSummitGradientPurple;
  }
}

export default function MiniSummitPreview({
  summit,
  onPress,
  active,
}: {
  summit: SummitType;
  onPress: () => void;
  active: boolean;
}) {
  const [now, setNow] = useState(new Date());

  const updateTime = useCallback(() => {
    if (active) {
      setNow(new Date());
    }
  }, [active]);

  useInterval(updateTime, 1000, active);

  const height = 150;

  const gradientColor = active
    ? getSummitGradientColor(summit)
    : upduoSummitGradientGray;

  return (
    <TouchableOpacity
      key={summit.id}
      onPress={onPress}
      style={{
        height,
        width: "100%",
        marginBottom: 20,
        position: "relative",
        borderRadius: 10,
        overflow: "hidden",
      }}
    >
      <div
        className={"black-and-white"}
        style={{ width: "100%", height: "100%" }}
      >
        {summit.imageUrl ? (
          <Image
            style={{
              height: "100%",
              width: "100%",
            }}
            resizeMode={"cover"}
            source={{
              uri: getResizedImageUrl(summit.imageUrl, 0, height),
            }}
          />
        ) : null}
      </div>
      <LinearGradient
        colors={[
          gradientColor,
          "rgba(255, 255, 255, 0)" /*colorWithOpacity(gradientColor, 0.1) */,
        ]}
        start={{ x: 0, y: 0 }}
        end={active ? { x: 1, y: 1 } : { x: 0, y: 1 }}
        locations={active ? [0.24, 1.41] : undefined}
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
        }}
      />
      <View
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          paddingHorizontal: 12,
          paddingVertical: 20,
          display: "flex",
        }}
      >
        {active ? (
          <CountdownTimer
            currentTime={now}
            countdownEnd={new Date(summit.startDate)}
            color={skaleWhite}
            style={{
              alignSelf: "flex-start",
              marginBottom: 10,
            }}
          />
        ) : null}
        <Text
          isHeading={true}
          style={{
            fontSize: 16,
            color: skaleWhite,
          }}
          ellipsizeMode={"tail"}
          numberOfLines={3}
        >
          {summit.name}
        </Text>
      </View>
    </TouchableOpacity>
  );
}
